import React, { useEffect, useState } from 'react';
import { Modal, Button, Collapse, Input, Popover, Tag, notification } from 'antd';
import { DislikeOutlined, EditOutlined, FlagOutlined, LikeOutlined } from '@ant-design/icons';
import { BASE_URL, parseWarningsAndErrors } from '../conf/config';
import { useUser } from '../UserContext';
import './DocumentImportPop.css';

const ACTION = { new: 'New', update: 'Change', unchanged: 'Unchanged', missing: 'Not in the order' };
const RANK = { red: 0, yellow: 1, green: 2, grey: 3 };

// old -> new, or just the value when nothing changed
const Change = ({ value }) => {
  if (!value || (value.old == null && value.new == null)) return null;
  if (value.old == null || value.old === value.new) return <span>{value.new}</span>;
  return <span><span className="di-old">{value.old}</span> → <b>{value.new}</b></span>;
};

const asRead = (row) => ({ CPN: row.CPN || '', quantity: row.quantity?.new || '', delivery_date: row.delivery_date || '' });
const deDate = (iso) => (iso ? iso.split('-').reverse().join('.') : '');

// what the AI read, under a value the user corrected
const ReadAs = ({ row, field, show = (v) => v }) =>
  row.read && String(row.read[field]) !== String(asRead(row)[field])
    ? <div className="di-read">AI read: {show(row.read[field]) || '—'}</div> : null;

/**
 * What the order import read from a purchase order: one row per position, coloured by the import rules
 * (backend order_import.py). A row can be corrected before accepting (checked again on the server) or
 * marked as read wrong; accepting sends its payload as the usual add/edit request. What was accepted,
 * corrected or marked goes to /api/order-import/<job>/feedback/ (Django admin: Import feedback).
 */
const DocumentImportPop = ({ result, onClose }) => {
  const { fetchWithAuth, refreshOrders, addNewLog } = useUser();
  const [rows, setRows] = useState([]); // the proposal; a corrected row is replaced by its new check
  const [done, setDone] = useState({}); // row index -> 'ok' | error text
  const [editing, setEditing] = useState({}); // row index -> values being corrected
  const [flagged, setFlagged] = useState({}); // row index -> true
  const [notes, setNotes] = useState({}); // row index (or 'reading') -> note being written
  const [rated, setRated] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRows(result ? result.rows.map((row, index) => ({ ...row, index })) : []);
    setDone({}); setEditing({}); setFlagged({}); setNotes({}); setRated(null);
  }, [result]);
  if (!result) return null;

  const call = (path, body) => fetchWithAuth(`${BASE_URL}/api/order-import/${result.job}/${path}/`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  // feedback never holds up the import
  const feedback = (body) => (result.job ? call('feedback', body).catch(() => null) : Promise.resolve(null));

  const open = rows.filter((r) => r.colour !== 'grey').sort((a, b) => RANK[a.colour] - RANK[b.colour] || a.index - b.index);
  const unchanged = rows.filter((r) => r.colour === 'grey');
  const counts = rows.reduce((acc, r) => ({ ...acc, [r.colour]: (acc[r.colour] || 0) + 1 }), {});
  const greens = open.filter((r) => r.colour === 'green' && r.payload && !done[r.index] && !editing[r.index]);

  const accept = async (row) => {
    try {
      const response = await fetchWithAuth(`${BASE_URL}/api/orders/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(row.payload),
      });
      const data = await response.json();
      const { parsedWarnings, parsedErrors } = parseWarningsAndErrors(data);
      addNewLog(`import from ${result.file}`, parsedWarnings, parsedErrors, row.payload);
      setDone((prev) => ({ ...prev, [row.index]: parsedErrors.length ? parsedErrors : 'ok' }));
      if (!parsedErrors.length && row.line != null) feedback({ row: row.index, saved: asRead(row) });
      return !parsedErrors.length;
    } catch (e) {
      setDone((prev) => ({ ...prev, [row.index]: 'The server could not be reached.' }));
      return false;
    }
  };

  const acceptGreens = async () => {
    setBusy(true);
    let failed = 0;
    for (const row of greens) {
      if (!(await accept(row))) failed += 1;
    }
    setBusy(false);
    notification[failed ? 'warning' : 'success']({
      message: failed ? `${failed} of ${greens.length} could not be saved` : `${greens.length} accepted`,
      duration: 4,
    });
  };

  const edit = (row, field, value) => setEditing((prev) => ({ ...prev, [row.index]: { ...prev[row.index], [field]: value } }));
  const stopEditing = (row) => setEditing(({ [row.index]: _, ...rest }) => rest);

  // the corrected values, checked again by the server with the same rules as the reading
  const check = async (row) => {
    try {
      const response = await call('check', { row: row.index, ...editing[row.index] });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        notification.error({ message: 'Could not check the correction', description: data.errors?.[0] });
        return;
      }
      setRows((prev) => prev.map((r) => (r.index === row.index ? { ...data.row, index: row.index, read: row.read || asRead(row) } : r)));
      stopEditing(row);
    } catch (e) {
      notification.error({ message: 'Could not check the correction', description: 'The server could not be reached.' });
    }
  };

  const markWrong = async (row) => {
    await feedback({ row: row.index, wrong: true, note: notes[row.index] || '' });
    setFlagged((prev) => ({ ...prev, [row.index]: true }));
  };

  const rate = async (rating) => {
    await feedback({ rating, comment: notes.reading || '' });
    setRated(rating);
  };

  const close = () => {
    refreshOrders();
    onClose();
  };

  const noteBox = (key, onSend, placeholder) => (
    <div className="di-note">
      <Input.TextArea rows={3} placeholder={placeholder} value={notes[key] || ''}
        onChange={(e) => setNotes((prev) => ({ ...prev, [key]: e.target.value }))} />
      <Button size="small" type="primary" onClick={onSend}>Send</Button>
    </div>
  );

  const table = (list) => (
    <table className="di-table">
      <thead>
        <tr>
          <th />
          <th>Order / pos.</th>
          <th>Customer</th>
          <th>Part number</th>
          <th>Quantity</th>
          <th>Ship date (planned)</th>
          <th>Why</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {list.map((row) => {
          const state = done[row.index];
          const values = editing[row.index];
          const correctable = row.line != null && !state;
          return (
            <tr key={row.index} className={`di-${row.colour}`}>
              <td className="di-bar"><Tag className={`di-tag di-tag-${row.colour}`}>{ACTION[row.action] || row.action}</Tag></td>
              <td>{row.order_ID} / {row.position_ID || '?'}</td>
              <td>{row.customer || <i>unknown</i>}</td>
              <td>
                {values ? <Input size="small" value={values.CPN} onChange={(e) => edit(row, 'CPN', e.target.value)} />
                  : <>{row.CPN}<ReadAs row={row} field="CPN" /><div className="di-sub">{row.description}</div></>}
              </td>
              <td>
                {values ? <Input size="small" value={values.quantity} onChange={(e) => edit(row, 'quantity', e.target.value)} />
                  : <><Change value={row.quantity} /><ReadAs row={row} field="quantity" /></>}
              </td>
              <td>
                {values
                  ? <><Input size="small" type="date" value={values.delivery_date} onChange={(e) => edit(row, 'delivery_date', e.target.value)} />
                    <div className="di-sub">delivery date</div></>
                  : <>
                    <Change value={row.shipping_date_planned} />
                    {row.delivery_date && <div className="di-sub">delivery {deDate(row.delivery_date)}</div>}
                    <ReadAs row={row} field="delivery_date" show={deDate} />
                  </>}
              </td>
              <td className="di-why">{row.reasons.map((r) => <div key={r}>{r}</div>)}</td>
              <td className="di-act">
                {state === 'ok' && <span className="di-ok">✓ saved</span>}
                {state && state !== 'ok' && <span className="di-err">{String(state)}</span>}
                {values && (
                  <>
                    <Button size="small" type="primary" onClick={() => check(row)}>Check</Button>
                    <Button size="small" onClick={() => stopEditing(row)}>Cancel</Button>
                  </>
                )}
                {!state && !values && row.payload && (
                  <Button size="small" type={row.colour === 'green' ? 'primary' : 'default'} danger={row.colour === 'red'}
                    onClick={() => accept(row)}>
                    {row.action === 'missing' ? 'Cancel position' : 'Accept'}
                  </Button>
                )}
                {correctable && !values && (
                  <Button size="small" icon={<EditOutlined />} title="Correct what the AI read"
                    onClick={() => setEditing((prev) => ({ ...prev, [row.index]: asRead(row) }))} />
                )}
                {row.line != null && (flagged[row.index]
                  ? <span className="di-flagged" title="Reported as read wrong"><FlagOutlined /></span>
                  : (
                    <Popover trigger="click" title="The AI read this row wrong"
                      content={noteBox(row.index, () => markWrong(row), 'What is wrong? (optional)')}>
                      <Button size="small" icon={<FlagOutlined />} title="Read wrong" />
                    </Popover>
                  ))}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <Modal open={!!result} onCancel={close} footer={null} width="90vw" title={`Purchase order import (AI): ${result.file}`} maskClosable={false}>
      <div className="di-head">
        <div>
          {result.documents.map((d) => (
            <div key={d.order_number}><b>{d.order_number}</b> · {d.customer || 'customer unknown'} · {d.lines} position{d.lines === 1 ? '' : 's'}</div>
          ))}
          <div className="di-sub">
            Read {result.route === 'vision' ? 'from the image (scan or photo)' : 'as text'}
            {result.text_problems?.length ? ` after the text did not add up (${result.text_problems.join('; ')})` : ''}.
            {' '}Check every row: the AI only reads, you decide. <EditOutlined /> corrects a row, <FlagOutlined /> reports it as read wrong.
          </div>
        </div>
        <div className="di-counts">
          {['green', 'yellow', 'red', 'grey'].filter((c) => counts[c]).map((c) => (
            <Tag key={c} className={`di-tag di-tag-${c}`}>{counts[c]} {c === 'grey' ? 'unchanged' : c}</Tag>
          ))}
          <Button type="primary" disabled={!greens.length || busy} loading={busy} onClick={acceptGreens}>
            Accept all green ({greens.length})
          </Button>
        </div>
      </div>
      {open.length ? table(open) : <div className="di-sub">Nothing to do: everything is as we have it.</div>}
      {unchanged.length > 0 && (
        <Collapse ghost items={[{ key: 'grey', label: `${unchanged.length} unchanged, nothing to do`, children: table(unchanged) }]} />
      )}
      {result.job && (
        <div className="di-rating">
          {rated ? <span className="di-sub">Thanks, that helps to make the reading better.</span> : (
            <>
              <span className="di-sub">Was this reading right?</span>
              <Button size="small" icon={<LikeOutlined />} onClick={() => rate(1)} />
              <Popover trigger="click" title="What went wrong?" content={noteBox('reading', () => rate(-1), 'e.g. the dates were all wrong (optional)')}>
                <Button size="small" icon={<DislikeOutlined />} />
              </Popover>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};

export default DocumentImportPop;
